import * as React from "react";
import { ChevronDown } from "lucide-react";
import * as t from "../../../ui/components/Table/Table.css";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import { Avatar } from "../../../ui/components/Avatar";
import { normalizeAvatarUrl } from "../../lib/profile-avatar-image";
import { ContextButton } from "../../../ui/components/ListView/components/ContextButton";
import type { ContextButtonAction } from "../../../ui/components/ListView/components/ContextButton";
import type { ListViewContextAction } from "../../../ui/components/ListView";
import { DropdownMenu, MenuItem } from "../../../ui/components/DropdownMenu";
import { useI18n } from "../../lib/i18n";
interface OrganizationRoleOption { id: string; name: string; color?: string | null }
import type { MembershipRow } from "./membershipListTypes";
import {
  formatMemberRoleLabel,
  formatInviteExpiry,
} from "./membershipListTypes";

export interface BuildMembershipColumnsOpts {
  isOrgAdmin: boolean;
  isSuperAdmin: boolean;
  currentUserId?: string;
  roleOptions: OrganizationRoleOption[];
  actionLoading: boolean;
  isSoleAdmin: (rowId: string) => boolean;
  onApprove: (rowId: string) => void;
  onDeny: (rowId: string) => void;
  onRoleChange: (rowId: string, newRoleId: string) => void;
  onRemove: (rowId: string) => void;
  onCancelInvite: (rowId: string) => void;
  onRestore: (rowId: string) => void;
  getContextActions?: (row: MembershipRow) => ListViewContextAction[];
}

function roleChipStyle(color?: string | null): React.CSSProperties {
  if (!color) {
    return {
      background: "var(--color-muted)",
      color: "var(--color-fg)",
      border: "1px solid var(--color-border)",
    };
  }
  return {
    background: `${color}18`,
    color,
    border: `1px solid ${color}40`,
  };
}

function RoleSelectDropdown({
  row,
  roleOptions,
  actionLoading,
  roleDropdownDisabled,
  onRoleChange,
}: {
  row: MembershipRow;
  roleOptions: OrganizationRoleOption[];
  actionLoading: boolean;
  roleDropdownDisabled: boolean;
  onRoleChange: (newRoleId: string) => void;
}) {
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const [open, setOpen] = React.useState(false);
  const disabled = actionLoading || roleDropdownDisabled;
  const selected =
    roleOptions.find((r) => r.id === row.roleId) ??
    (row.roleName || row.roleLabel
      ? {
          id: row.roleId ?? "",
          name: row.roleLabel?.trim() || row.roleName || "",
          color: row.roleColor,
        }
      : null);
  const label = selected
    ? formatMemberRoleLabel(selected.name)
    : "Select role";

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        disabled={disabled}
        aria-label={`Role for ${row.displayName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) setOpen((v) => !v);
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
          fontSize: 11,
          fontWeight: 500,
          padding: "2px 8px",
          borderRadius: 9999,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
          ...roleChipStyle(selected?.color ?? row.roleColor),
        }}
      >
        {label}
        <ChevronDown size={10} aria-hidden />
      </button>
      <DropdownMenu
        anchorEl={anchorRef}
        anchorPos="SE"
        openDirection="SW"
        open={open}
        setOpen={setOpen}
        closeOnSelect
        onChange={(id) => {
          if (id && id !== (row.roleId ?? "")) onRoleChange(id);
        }}
      >
        {roleOptions.map((role) => (
          <MenuItem
            key={role.id}
            value={role.id}
            disabled={role.id === row.roleId || disabled}
          >
            <span style={role.color ? { color: role.color } : undefined}>
              {formatMemberRoleLabel(role.name)}
            </span>
          </MenuItem>
        ))}
      </DropdownMenu>
    </>
  );
}

function RoleCell({
  row,
  isOrgAdmin,
  roleOptions,
  actionLoading,
  roleDropdownDisabled,
  onRoleChange,
}: {
  row: MembershipRow;
  isOrgAdmin: boolean;
  roleOptions: OrganizationRoleOption[];
  actionLoading: boolean;
  roleDropdownDisabled: boolean;
  onRoleChange: (newRoleId: string) => void;
}) {
  if (row.kind === "active" && isOrgAdmin && roleOptions.length > 0) {
    return (
      <RoleSelectDropdown
        row={row}
        roleOptions={roleOptions}
        actionLoading={actionLoading}
        roleDropdownDisabled={roleDropdownDisabled}
        onRoleChange={onRoleChange}
      />
    );
  }

  if (row.roleName || row.roleLabel) {
    return (
      <span
        style={{
          display: "inline-flex",
          padding: "2px 8px",
          borderRadius: 9999,
          fontSize: 11,
          fontWeight: 500,
          ...roleChipStyle(row.roleColor),
        }}
      >
        {formatMemberRoleLabel(row.roleName, row.roleLabel)}
      </span>
    );
  }

  return (
    <span style={{ color: "var(--color-fgMuted)", fontSize: 13 }}>—</span>
  );
}

function StatusCell({ row }: { row: MembershipRow }) {
  const { t: tr } = useI18n();
  if (row.kind === "pending")
    return <span className={`${t.badge} ${t.badgePending}`}>{tr("pro.orgs.statusPending")}</span>;
  if (row.kind === "invited")
    return (
      <span
        className={t.badge}
        style={{ background: "#E0E7FF", color: "#3730A3" }}
      >
        Invited
      </span>
    );
  if (row.deletedAt)
    return <span className={`${t.badge} ${t.badgeOffline}`}>{tr("pro.orgs.statusDisabled")}</span>;
  return (
    <span className={`${t.badge} ${t.badgeOnline}`}>
      <span className={`${t.badgeDot} ${t.dotOnline}`} />
      Active
    </span>
  );
}

function ActionsCell({
  row,
  isOrgAdmin,
  actionLoading,
  onApprove,
  onDeny,
  getContextActions,
}: {
  row: MembershipRow;
  isOrgAdmin: boolean;
  actionLoading: boolean;
  onApprove: () => void;
  onDeny: () => void;
  getContextActions?: (row: MembershipRow) => ListViewContextAction[];
}) {
  if (row.kind === "pending" && isOrgAdmin) {
    return (
      <div
        style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          disabled={actionLoading}
          onClick={onApprove}
          style={{
            padding: "4px 10px",
            fontSize: 12,
            fontWeight: 600,
            borderRadius: 6,
            border: "none",
            cursor: actionLoading ? "not-allowed" : "pointer",
            background: "var(--color-accent)",
            color: "#fff",
            opacity: actionLoading ? 0.6 : 1,
          }}
        >
          Approve
        </button>
        <button
          type="button"
          disabled={actionLoading}
          onClick={onDeny}
          style={{
            padding: "4px 10px",
            fontSize: 12,
            fontWeight: 600,
            borderRadius: 6,
            border: "1px solid var(--color-border)",
            cursor: actionLoading ? "not-allowed" : "pointer",
            background: "var(--color-card)",
            color: "var(--color-fg)",
            opacity: actionLoading ? 0.6 : 1,
          }}
        >
          Deny
        </button>
      </div>
    );
  }

  const menuActions: ContextButtonAction[] = (getContextActions?.(row) ?? [])
    .map(a => ({ id: a.id, label: a.label, icon: a.icon, destructive: a.danger, disabled: a.disabled, disabledTooltip: a.disabledTooltip, onSelect: () => a.onClick([row.id]) }));

  if (menuActions.length === 0) return null;

  return (
    <div
      style={{ display: "flex", justifyContent: "flex-end" }}
      onClick={(e) => e.stopPropagation()}
    >
      <ContextButton
        actions={menuActions}
        disabled={actionLoading}
        ariaLabel={`Actions for ${row.displayName}`}
      />
    </div>
  );
}

export function buildMembershipColumns(
  opts: BuildMembershipColumnsOpts,
): ColumnDefinition<MembershipRow>[] {
  const {
    isOrgAdmin,
    isSuperAdmin,
    currentUserId,
    roleOptions,
    actionLoading,
    isSoleAdmin,
    onApprove,
    onDeny,
    onRoleChange,
    onRemove,
    onCancelInvite,
    onRestore,
    getContextActions,
  } = opts;

  return [
    {
      id: "member",
      label: "Member",
      sortable: true,
      sort: (a, b) => a.displayName.localeCompare(b.displayName),
      render: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
          <div style={{ flexShrink: 0 }}>
            <Avatar
              email={row.email}
              firstName={row.firstName ?? ""}
              lastName={row.lastName ?? ""}
              image={
                row.kind !== "invited"
                  ? normalizeAvatarUrl(row.avatarUrl) || undefined
                  : undefined
              }
              size={32}
            />
          </div>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontWeight: 600,
                fontSize: 13,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.displayName}
            </div>
            {row.kind !== "invited" && (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--color-fgSecondary)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {row.email}
              </div>
            )}
            {row.kind === "invited" && row.inviteExpiresAt && (
              <div style={{ fontSize: 11, color: "var(--color-fgMuted)" }}>
                {formatInviteExpiry(row.inviteExpiresAt)}
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "role",
      label: "Role",
      width: 160,
      render: (row) => (
        <RoleCell
          row={row}
          isOrgAdmin={isOrgAdmin}
          roleOptions={roleOptions}
          actionLoading={actionLoading}
          roleDropdownDisabled={isSoleAdmin(row.id)}
          onRoleChange={(newRoleId) => onRoleChange(row.id, newRoleId)}
        />
      ),
    },
    {
      id: "status",
      label: "Status",
      width: 100,
      render: (row) => <StatusCell row={row} />,
    },
    {
      id: "actions",
      label: "",
      width: 148,
      enableHiding: false,
      render: (row) => (
        <ActionsCell
          row={row}
          isOrgAdmin={isOrgAdmin}
          actionLoading={actionLoading}
          onApprove={() => onApprove(row.id)}
          onDeny={() => onDeny(row.id)}
          getContextActions={getContextActions}
        />
      ),
    },
  ];
}
